import { useEffect, useState } from "react";

import Box from "@/components/Box";
import Grid from "@/components/Grid";
import Textarea from "@/components/Textarea";
import UserTestMultiSelect from "@/components/UserTestMultiSelect";
import {
  APPOINTMENT_TYPE_WHICH_TEST,
  APPOINTMENT_TYPE_1_X_TEST_RESULT,
  APPOINTMENT_TYPE_2_X_TEST_RESULTS,
  APPOINTMENT_TYPE_REQUEST_CALLBACK,
  APPOINTMENT_TYPE_FIRST_TEST_RESULTS
} from "@/components/organisations/constants";
import PatientDetails from "@/components/organisations/shared/PatientDetails";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { theme } from "@/core/theme";
import { CREATE_APPOINTMENT_MUTATION } from "@/graphql/organisations/mutations";
import { APPOINTMENT_TYPES_QUERY } from "@/graphql/organisations/queries/appointments";
import { USER_TEST_FIELDS_TRUNCATED } from "@/graphql/tpo/results/types";
import DesktopIcon from "@/images/DesktopIcon";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import Modal from "@/tpo/Modal";
import Radio from "@/tpo/Radio";
import SelectPatientField from "@/tpo/SelectPatient";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";
import { gql, useQuery, useMutation } from "@apollo/client";
import * as Sentry from "@sentry/browser";
import styled from "styled-components";
import PhoneRingingIcon from "@/images/PhoneRingingIcon";
import { ExternalTextLink } from "@/components/Links";
import { SpanText } from "@/components/Text";

const USER_TEST_DETAIL_QUERY = gql`
  query UserTestDetailQuery($userId: ID!, $userTestId: ID!, $organisationId: ID!) {
    userTest(userId: $userId, userTestId: $userTestId, organisationId: $organisationId) {
      ...UserTestFieldsTruncated
      testProduct {
        id
        name
      }
      orderTestItem {
        id
        order {
          id
          checkoutDate
        }
      }
    }
  }
  ${USER_TEST_FIELDS_TRUNCATED}
`;

const STEPS = [
  { id: 0, label: "consultation type" },
  { id: 1, label: "patient" },
  { id: 2, label: "test" },
  { id: 3, label: "confirm and submit" }
];

// Helper function for case-insensitive appointment type name comparison
const isAppointmentType = (type, name) => {
  return type?.name?.toLowerCase() === name?.toLowerCase();
};

// Helper function to determine if appointment type should skip to final step (no patient, no test)
const shouldSkipToFinal = consultationType => {
  return (
    isAppointmentType(consultationType, APPOINTMENT_TYPE_WHICH_TEST) ||
    isAppointmentType(consultationType, APPOINTMENT_TYPE_REQUEST_CALLBACK)
  );
};

// Helper function to determine if appointment type needs patient but not test selection
const shouldSkipTestStep = consultationType => {
  return isAppointmentType(consultationType, APPOINTMENT_TYPE_FIRST_TEST_RESULTS);
};

function Breadcrumbs({ currentStep, onStepClick, selectedConsultationType }) {
  // Determine which steps to show based on consultation type
  let relevantSteps = STEPS;
  if (selectedConsultationType) {
    if (shouldSkipToFinal(selectedConsultationType)) {
      relevantSteps = [STEPS[0], STEPS[3]];
    } else if (shouldSkipTestStep(selectedConsultationType)) {
      relevantSteps = [STEPS[0], STEPS[1], STEPS[3]];
    }
  }

  return (
    <Group gap={16} alignItems="center" mb={40} flexWrap="wrap">
      {relevantSteps.map((step, index) => (
        <Group key={step.id} gap={16} alignItems="center">
          <Box
            fontFamily={currentStep === step.id ? "gilroyBold" : "gilroyMedium"}
            fontSize={[12]}
            letterSpacing="2.8px"
            color={currentStep >= step.id ? "dark" : "lightGrey"}
            style={{
              cursor: currentStep >= step.id ? "pointer" : "default",
              textTransform: "uppercase"
            }}
            onClick={() => {
              if (currentStep >= step.id && onStepClick) {
                onStepClick(step.id);
              }
            }}
          >
            {step.label}
          </Box>
          {index < relevantSteps.length - 1 && (
            // vertical line
            <Box width="1px" height={16} bg="dark" />
          )}
        </Group>
      ))}
    </Group>
  );
}

const ConsultationCardContainer = styled(Group)`
  :hover {
    background-color: #ffccca;
  }
`;

function ConsultationCard({ name, description, duration, selected, onClick, ...props }) {
  return (
    <ConsultationCardContainer
      p={20}
      pr={28}
      borderWidth={1}
      borderColor="dark"
      borderStyle="solid"
      borderRadius={20}
      maxWidth={305}
      cursor={onClick ? "pointer" : "default"}
      gap={20}
      bg={selected ? "#ffccca" : "white"}
      onClick={onClick}
      {...props}
    >
      {name === APPOINTMENT_TYPE_REQUEST_CALLBACK ? <PhoneRingingIcon/> :  <DesktopIcon />}
      <Stack>
        <Box pb={16}>
          <Box fontFamily="gilroyBold" fontSize={[16, 16, 18]} lineHeight="28px">
            {name}
          </Box>
          <Box
            fontFamily={"gilroyMedium"}
            fontSize={[12]}
            lineHeight="120%"
          >
            {description}
          </Box>
        </Box>
        {duration && (
          <Box fontFamily="gilroyBold" fontSize={[14]} lineHeight="120%" mt="auto">
            {duration}
          </Box>
        )}
      </Stack>
    </ConsultationCardContainer>
  );
}

function ConsultationTypeView({ selectedType, onSelectType, appointmentTypes, loading }) {
  if (loading) {
    return (
      <Stack gap={[20, 20, 40]}>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
          Loading appointment types...
        </Box>
      </Stack>
    );
  }

  return (
    <Stack gap={[20, 20, 40]}>
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[20, 20, 28]} lineHeight="36px" mb={20}>
          1. What type of support call do you require?
        </Box>
        <Stack
          as="ul"
          fontFamily="gilroyMedium"
          fontSize={[14, 14, 16]}
          lineHeight="24px"
          color="gray"
          mb={4}
          listStyle="disc"
          style={{
            listStylePosition: "inside"
          }}
        >
          Select the consultation type that best suits your requirements. Consultation length is determined by the test being discussed.
          <Box color="inherit" as="li" fontFamily="inherit" fontSize="inherit" marginLeft={1}><SpanText fontFamily="gilroyBold">30 minutes:</SpanText> Cyrex, Immunoscience, Blood Profile, Cardiometabolic Profile, Stool Panels, DNA Testing, Microgen, DUTCH, HuMap, Organic Acid Tests</Box>
          <Box color="inherit" as="li" fontFamily="inherit" fontSize="inherit" marginLeft={1}><SpanText fontFamily="gilroyBold">15 minutes:</SpanText> All other tests</Box>
        </Stack>
      </Box>
      <Grid
        gridTemplateColumns={[
          "repeat(1, 1fr)",
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)"
        ]}
        gap={16}
      >
        {appointmentTypes?.map(appointmentType => (
          <ConsultationCard
            key={appointmentType.id}
            name={appointmentType.name}
            description={appointmentType.description}
            duration={appointmentType.duration}
            selected={selectedType?.id === appointmentType.id}
            onClick={() => onSelectType(appointmentType)}
          />
        ))}
      </Grid>
    </Stack>
  );
}

function SubmissionSuccessView({ scheduleMeetingUrl }) {
  return (
    <Stack gap={[20, 20, 40]}>
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[24, 24, 32]} lineHeight="36px" mb={20}>
          Finalise your appointment
        </Box>
        <Box
          fontFamily="gilroyMedium"
          fontSize={[14, 14, 16]}
          lineHeight="24px"
          color="dark"
          mb={40}
        >
          Your submission is now open in a new window, allowing you to schedule a time slot with one of our clinical advisors.
          <br/>
          <br/>
          If the appointment calendar does not open automatically, this may be due to your browser blocking new windows. You can launch the calendar directly using the button below to complete your booking.
          <br/>
          <br/>
          If you have any questions or need support, please contact info@regeneruslabs.com.
          <br/>
          <br/>
          {
            scheduleMeetingUrl && <ButtonV2 color='dark' as={ExternalTextLink} href={scheduleMeetingUrl} newTab withChevron underline={false} size={["sm", "sm", "md"]}>appointment scheduler</ButtonV2>
          }
        </Box>
      </Box>
    </Stack>
  );
}

function PatientView({ selectedPatientId, onSelectPatient, organisation }) {
  const [value, setValue] = useState(selectedPatientId || "");

  useEffect(() => {
    setValue(selectedPatientId || "");
  }, [selectedPatientId]);

  const patient = organisation?.patients.find(p => p.id === value);

  return (
    <Stack gap={[20, 20, 28]}>
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[20, 20, 28]} lineHeight="36px" mb={20}>
          2. Select your patient
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="24px" color="gray">
          Please select the patient whose test results you would like to discuss.
        </Box>
      </Box>
      <SelectPatientField
        patients={organisation?.patients || []}
        value={value}
        onChange={v => {
          setValue(v);
          onSelectPatient(v);
        }}
        labelProps={{
          fontFamily: "gilroyBold",
          fontSize: 18,
          mb: 2
        }}
      />
      {patient && (
        <Box>
          <PatientDetails patient={patient} />
        </Box>
      )}
    </Stack>
  );
}

function UserTestDetailTile({ userTestId, userId, title, showRadio, isPrimary, onPrimaryChange, organisationId }) {
  const { data } = useQuery(USER_TEST_DETAIL_QUERY, {
    variables: {
      userId: userId,
      userTestId: userTestId,
      organisationId: organisationId
    },
    skip: !userTestId || !userId
  });

  const userTest = data?.userTest;
  const order = userTest?.orderTestItem?.order;
  const orderNumber = order?.id;
  const orderDate = order?.checkoutDate;

  if (!userTest) return null;

  return (
    <Box bg="white" p={16} borderRadius={12} border="1px solid" borderColor="haze">
      <Stack gap={20}>
        <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]} color="gray">
          {title}
        </Box>
        <Group gap={5}>
          <Box>Name:</Box>
          <Box>{userTest.testProduct?.name}</Box>
        </Group>
        {orderNumber && (
          <Group gap={5}>
            <Box>Order number:</Box>
            <Box>{orderNumber}</Box>
          </Group>
        )}
        {orderDate && (
          <Group gap={5}>
            <Box>Order date:</Box>
            <Box>{new Date(orderDate).toLocaleDateString("en-GB")}</Box>
          </Group>
        )}
        {showRadio && (
          <Radio
            name="primaryTest"
            value={userTestId}
            checked={isPrimary}
            onChange={value => onPrimaryChange && onPrimaryChange(value)}
            label="Primary test*"
          />
        )}
      </Stack>
    </Box>
  );
}

function TestView({
  selectedUserTestId,
  onSelectUserTest,
  selectedUserTestIds,
  onSelectUserTestIds,
  selectedConsultationType,
  selectedPatientId,
  organisation,
  primaryUserTestId,
  onPrimaryUserTestChange
}) {
  // Get the userId from the selected patient
  const patient = organisation?.patients.find(p => p.id === selectedPatientId);
  const userId = patient?.patientProfile?.pk;

  // Determine what to show based on consultation type
  const showSingleUserTest = isAppointmentType(
    selectedConsultationType,
    APPOINTMENT_TYPE_1_X_TEST_RESULT
  );
  const showTwoUserTests = isAppointmentType(
    selectedConsultationType,
    APPOINTMENT_TYPE_2_X_TEST_RESULTS
  );

  return (
    <Stack gap={[20, 20, 40]}>
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[20, 20, 28]} lineHeight="36px" mb={20}>
          3. Select your patient&apos;s test
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="24px" color="gray">
          {showSingleUserTest &&
            "Please choose an existing test or an alternative test that you would like to discuss regarding your patient. This information will help us connect you with the most suitable clinical advisor from our team."}
          {showTwoUserTests &&
            "Please choose an existing test or an alternative test that you would like to discuss regarding your patient. This information will help us connect you with the most suitable clinical advisor from our team."}
        </Box>
      </Box>
      {showSingleUserTest && (
        <Stack gap={40}>
          <UserTestMultiSelect
            value={selectedUserTestId}
            onChange={onSelectUserTest}
            userId={userId}
            placeholder="Select patient's test"
            singleSelect={true}
            organisationId={organisation?.id}
            sx={{
              mr: 'auto'
            }}
          />
          {selectedUserTestId && userId && (
            <UserTestDetailTile
              userTestId={selectedUserTestId}
              userId={userId}
              title="Test slot 1"
              organisationId={organisation?.id}
            />
          )}
        </Stack>
      )}

      {showTwoUserTests && (
        <Stack gap={40}>
          <UserTestMultiSelect
            value={selectedUserTestIds}
            onChange={onSelectUserTestIds}
            userId={userId}
            placeholder="Select up to 2 test results..."
            maxSelections={2}
            title="Test slots"
            organisationId={organisation?.id}
            sx={{
              mr: 'auto'
            }}
          />
          {selectedUserTestIds.length > 0 && userId && (
            <>
              <Group gap={16}>
                {selectedUserTestIds.map((userTestId, index) => (
                  <UserTestDetailTile
                    key={userTestId}
                    userTestId={userTestId}
                    userId={userId}
                    title={`Test slot ${index + 1}`}
                    showRadio={true}
                    isPrimary={primaryUserTestId === userTestId}
                    onPrimaryChange={onPrimaryUserTestChange}
                    organisationId={organisation?.id}
                  />
                ))}
              </Group>
              <Box fontFamily="gilroyMedium" fontSize={[12, 12, 14]} color="gray" mt={-20}>
                **The primary test is the key assessment that we should concentrate on first during your call.
              </Box>
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function ConfirmAndSubmitView({
  selectedConsultationType,
  selectedPatientId,
  selectedUserTestId,
  selectedUserTestIds,
  organisation,
  additionalInformation,
  onAdditionalInformationChange,
  primaryUserTestId,
  onPrimaryUserTestChange
}) {
  const patient = organisation?.patients.find(p => p.id === selectedPatientId);
  const userId = patient?.patientProfile?.pk;

  // Determine what to show based on consultation type
  const showSingleUserTest =
    isAppointmentType(selectedConsultationType, APPOINTMENT_TYPE_1_X_TEST_RESULT) &&
    selectedUserTestId &&
    userId;
  const showTwoUserTests =
    isAppointmentType(selectedConsultationType, APPOINTMENT_TYPE_2_X_TEST_RESULTS) &&
    selectedUserTestIds.length > 0 &&
    userId;

  return (
    <Stack gap={[20, 20, 40]}>
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[20, 20, 28]} lineHeight="36px" mb={20}>
          4. Confirm and Submit
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="24px" color="gray">
          Please check that the below details reflect the consultation you require before submitting. 
          Once you submit you will be taken to a booking calendar to finalise your appointment.
        </Box>
      </Box>

      {/* Consultation Type - Reuse ConsultationCard */}
      {selectedConsultationType && (
        <Box>
          <Grid
            gridTemplateColumns={[
              "repeat(1, 1fr)",
              "repeat(1, 1fr)",
              "repeat(2, 1fr)",
              "repeat(3, 1fr)"
            ]}
            gap={16}
          >
            <ConsultationCard
              name={selectedConsultationType.name}
              description={selectedConsultationType.description}
              duration={selectedConsultationType.duration}
              selected={true}
            />
          </Grid>
        </Box>
      )}

      {/* Patient Details - Only show if patient is selected */}
      {patient && (
        <Box>
          <PatientDetails patient={patient} />
        </Box>
      )}

      {/* Test Details - Test Product - Not shown for "Which test?" option */}

      {/* Test Details - Single User Test - Reuse UserTestDetailTile */}
      {showSingleUserTest && (
        <Box>
          <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]} color="gray" mb={12}>
            Test Result
          </Box>
          <UserTestDetailTile userTestId={selectedUserTestId} userId={userId} title="Test slot 1" organisationId={organisation?.id} />
        </Box>
      )}

      {/* Test Details - Two User Tests - Reuse UserTestDetailTile */}
      {showTwoUserTests && (
        <Box>
          <Group gap={16} mb={40}>
            {selectedUserTestIds.map((userTestId, index) => (
              <UserTestDetailTile
                key={userTestId}
                userTestId={userTestId}
                userId={userId}
                title={`Test slot ${index + 1}`}
                showRadio={true}
                isPrimary={primaryUserTestId === userTestId}
                onPrimaryChange={onPrimaryUserTestChange}
                organisationId={organisation?.id}
              />
            ))}
          </Group>
          <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>*The primary test is the key assessment that we should concentrate on first during your call.</Box>
        </Box>
      )}

      {/* Additional Information Textarea */}
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[20, 20, 28]} lineHeight="36px" mb={20}>
          Case Summary (Required)
        </Box>
        <Box mb={40}>Provide a brief case summary and key questions to help your clinical advisor prepare (max. 250 words).</Box>
        <Textarea
          placeholder="Enter details"
          value={additionalInformation}
          onChange={e => onAdditionalInformationChange(e.target.value)}
          rows={4}
          bg="white"
          px={20}
          py={15}
          borderRadius={5}
          border="1px solid"
          borderColor="haze"
          style={{
            width: "100%",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical"
          }}
        />
      </Box>
    </Stack>
  );
}

function renderStepView(step, props) {
  if (props.submissionSuccess) {
    return <SubmissionSuccessView onStartAgain={props.onStartAgain} scheduleMeetingUrl={props.scheduleMeetingUrl} />;
  }

  switch (step) {
    case 0:
      return (
        <ConsultationTypeView
          {...props}
          appointmentTypes={props.appointmentTypes}
          loading={props.appointmentTypesLoading}
        />
      );
    case 1:
      return <PatientView {...props} />;
    case 2:
      return <TestView {...props} />;
    case 3:
      return <ConfirmAndSubmitView {...props} />;
    default:
      return null;
  }
}

export default function BookSupportCallModal({ show, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedConsultationType, setSelectedConsultationType] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedUserTestId, setSelectedUserTestId] = useState(null);
  const [selectedUserTestIds, setSelectedUserTestIds] = useState([]);
  const [primaryUserTestId, setPrimaryUserTestId] = useState(null);
  const [additionalInformation, setAdditionalInformation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [scheduleMeetingUrl, setScheduleMeetingUrl] = useState(null);
  const { organisation } = useOrganisationContext();

  const { data: appointmentTypesData, loading: appointmentTypesLoading } =
    useQuery(APPOINTMENT_TYPES_QUERY);
  const appointmentTypes = appointmentTypesData?.appointmentTypes || [];
  const [createAppointment] = useMutation(CREATE_APPOINTMENT_MUTATION);

  // Get relevant steps based on consultation type
  const getRelevantSteps = () => {
    if (!selectedConsultationType) {
      return STEPS;
    }

    if (shouldSkipToFinal(selectedConsultationType)) {
      return [STEPS[0], STEPS[3]];
    }
    if (shouldSkipTestStep(selectedConsultationType)) {
      return [STEPS[0], STEPS[1], STEPS[3]];
    }
    return STEPS;
  };

  const handleNext = () => {
    const relevantSteps = getRelevantSteps();
    const currentIndex = relevantSteps.findIndex(step => step.id === currentStep);

    if (currentIndex < relevantSteps.length - 1) {
      const nextStep = relevantSteps[currentIndex + 1];
      setCurrentStep(nextStep.id);
    }
  };

  const handleBack = () => {
    const relevantSteps = getRelevantSteps();
    const currentIndex = relevantSteps.findIndex(step => step.id === currentStep);

    if (currentIndex > 0) {
      // Go to previous relevant step
      const previousStep = relevantSteps[currentIndex - 1];
      setCurrentStep(previousStep.id);
    }
  };

  const handleStepClick = step => {
    setCurrentStep(step);
  };

  const handleStartAgain = () => {
    setSubmissionSuccess(false);
    setCurrentStep(0);
    setSelectedConsultationType(null);
    setSelectedPatientId(null);
    setSelectedUserTestId(null);
    setSelectedUserTestIds([]);
    setPrimaryUserTestId(null);
    setAdditionalInformation("");
    setScheduleMeetingUrl(null);
  };

  const handleSelectConsultationType = type => {
    setSelectedConsultationType(type);
    // Reset all state when consultation type changes
    setSelectedPatientId(null);
    setSelectedUserTestId(null);
    setSelectedUserTestIds([]);
    setPrimaryUserTestId(null);
    setAdditionalInformation("");
    setCurrentStep(0);
  };

  const handleSelectPatient = patientId => {
    setSelectedPatientId(patientId);
    // Reset user test selections when patient changes
    setSelectedUserTestId(null);
    setSelectedUserTestIds([]);
    setPrimaryUserTestId(null);
    setAdditionalInformation("");
  };

  const handleSelectUserTest = userTestId => {
    setSelectedUserTestId(userTestId);
  };

  const handleSelectUserTestIds = userTestIds => {
    setSelectedUserTestIds(userTestIds);
    // If primary test is no longer in the selected tests, or no primary is set, auto-select first test
    if (userTestIds.length > 0) {
      if (!primaryUserTestId || !userTestIds.includes(primaryUserTestId)) {
        setPrimaryUserTestId(userTestIds[0]);
      }
    } else {
      setPrimaryUserTestId(null);
    }
  };

  const handlePrimaryUserTestChange = userTestId => {
    setPrimaryUserTestId(userTestId);
  };

  const handleClose = () => {
    if (submissionSuccess) {
      // Reset the flow so the next open starts from the first screen
      handleStartAgain();
    }

    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const windowReference = window.open();

    const patient = organisation.patients.find(p => p.id === selectedPatientId);

    if (!patient && !shouldSkipToFinal(selectedConsultationType)) {
      setSubmitting(false);
      return;
    }

    if (!selectedConsultationType?.id) {
      setSubmitting(false);
      return;
    }

    setSubmitting(true);

    createAppointment({
      variables: {
        input: {
          organisation: organisation.id,
          patientProfile: patient?.patientProfile?.pk || "",
          type: selectedConsultationType.id,
          additionalInformation: additionalInformation || "",
          userTests:
            selectedUserTestIds.length > 0
              ? selectedUserTestIds
              : selectedUserTestId
              ? [selectedUserTestId]
              : [],
          primaryUserTest: primaryUserTestId || null
        }
      }
    })
      .then(({ data }) => {
        if (data?.createAppointmentMutation?.errors?.length > 0) {
          setSubmitting(false);
        } else {
          // Success - show success screen with schedule meeting URL link if available
          const url = data?.createAppointmentMutation?.appointment?.scheduleMeetingUrl;
          if (url) {
            setScheduleMeetingUrl(url);
            windowReference.location = url;
          }

          setSubmitting(false);
          setSubmissionSuccess(true);
        }
      })
      .catch(error => {
        console.error("Error creating appointment:", error);
        Sentry.captureException(error);
        setSubmitting(false);
      });
  };

  return (
    <Modal
      show={show}
      close={handleClose}
      maxWidth={1024}
      mode="centered"
      closeButton
      bg="white"
      borderRadius={5}
      headerProps={{
        p: [20, 20, 40]
      }}
    >
      <Stack
        px={[20, 20, 40]}
        pb={[30, 30, 80]}
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {!submissionSuccess && (
          <Box alignSelf="flex-start">
            <Breadcrumbs
              currentStep={currentStep}
              onStepClick={handleStepClick}
              selectedConsultationType={selectedConsultationType}
            />
          </Box>
        )}

        <Box flex={1} minHeight={submissionSuccess ? undefined : 300} mb={4}>
          {renderStepView(currentStep, {
            selectedType: selectedConsultationType,
            onSelectType: handleSelectConsultationType,
            selectedPatientId: selectedPatientId,
            onSelectPatient: handleSelectPatient,
            organisation: organisation,
            selectedUserTestId: selectedUserTestId,
            onSelectUserTest: handleSelectUserTest,
            selectedUserTestIds: selectedUserTestIds,
            onSelectUserTestIds: handleSelectUserTestIds,
            selectedConsultationType: selectedConsultationType,
            additionalInformation: additionalInformation,
            onAdditionalInformationChange: setAdditionalInformation,
            appointmentTypes: appointmentTypes,
            appointmentTypesLoading: appointmentTypesLoading,
            submissionSuccess: submissionSuccess,
            onStartAgain: handleStartAgain,
            primaryUserTestId: primaryUserTestId,
            onPrimaryUserTestChange: handlePrimaryUserTestChange,
            scheduleMeetingUrl: scheduleMeetingUrl
          })}
        </Box>
        {!submissionSuccess &&
          (() => {
            const relevantSteps = getRelevantSteps();
            const currentIndex = relevantSteps.findIndex(step => step.id === currentStep);
            const isFirstStep = currentIndex === 0;
            const isLastStep = currentIndex === relevantSteps.length - 1;
            const isTestStep = currentStep === 2;

            // Validation for test selection step
            const isTestSelectionInvalid =
              isTestStep &&
              ((isAppointmentType(selectedConsultationType, APPOINTMENT_TYPE_1_X_TEST_RESULT) &&
                !selectedUserTestId) ||
                (isAppointmentType(
                  selectedConsultationType,
                  APPOINTMENT_TYPE_2_X_TEST_RESULTS
                ) &&
                  selectedUserTestIds.length < 2));

            return (
              <Group justifyContent="space-between" alignItems="center" mt="auto">
                {!isFirstStep && (
                  <ButtonV2
                    color="dark"
                    onClick={handleBack}
                    sx={{
                      color: "white"
                    }}
                  >
                    Back
                  </ButtonV2>
                )}
                <ButtonV2
                  ml="auto"
                  color="green"
                  onClick={isLastStep ? handleSubmit : handleNext}
                  disabled={
                    submitting ||
                    isTestSelectionInvalid ||
                    (isLastStep &&
                      (!organisation ||
                        (!shouldSkipToFinal(selectedConsultationType) && !selectedPatientId) ||
                        !additionalInformation.trim())) ||
                    (currentStep === 1 && !selectedPatientId)
                  }
                  rightIcon={
                    !isLastStep ? (
                      <ChevronComponent
                        direction="right"
                        fill={theme.colors.white}
                        style={{
                          marginLeft: "10px"
                        }}
                      />
                    ) : null
                  }
                >
                  {submitting ? "Submitting..." : isLastStep ? "Submit" : "Next"}
                </ButtonV2>
              </Group>
            );
          })()}
      </Stack>
    </Modal>
  );
}
